import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { TopCostPerKmDashboardDto } from 'api/dashboard/dtos/TopCostPerKmDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useInfiniteReveal from '../components/useInfiniteReveal';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from './TopCostPerKm.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

const formatCostPerKm = (value: number) => {
	return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC/km';
};

type Props = {
	// Legacy props (direct data passing)
	data?: TopCostPerKmDashboardDto[] | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const TopCostPerKm = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<TopCostPerKmDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [previousAvg, setPreviousAvg] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);

	const isLegacyMode = props.data !== undefined;
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topCostPerKm');

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			setPreviousAvg(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getTopCostPerKm(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo };
				const previous = await DashboardService.getTopCostPerKm(prevCriteria);
				if (!cancelled) {
					const list = previous || [];
					const avg = list.length > 0 ? list.reduce((s, x) => s + x.costPerKm, 0) / list.length : 0;
					setPreviousAvg(avg);
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousAvg(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const items = data || [];
	const maxCostPerKm = Math.max(...items.map(d => d.costPerKm), 0);
	const avgCostPerKm = items.length > 0 ? items.reduce((sum, it) => sum + it.costPerKm, 0) / items.length : 0;
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: TopCostPerKmDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box className={tableStyles.container} title={t('dashboard.top_cost_per_km') as string}
			subTitleTooltip={t('dashboard.top_cost_per_km_tooltip' as any) as string}
			renderOptions={!isLegacyMode ? <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} /> : undefined}>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={formatCostPerKm(avgCostPerKm)}
						subLabel={t('dashboard.avg_cost_per_km' as any) as string}
						currentTotal={avgCostPerKm}
						previousTotal={previousAvg}
						previousRange={previousRange}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const barWidth = maxCostPerKm > 0 ? (item.costPerKm / maxCostPerKm) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.vehicleId}
								className={styles.row}
								onClick={() => window.open(`/vehicles/details/${item.vehicleId}/monthlyCharges/list`, '_blank')}
							>
								<span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="top-cost-per-km-tooltip" data-place="right">{item.registrationNumber}</span></span>
								<div className={styles.barTrack}>
									<div
										className={styles.barFill}
										style={{ width: `${barWidth}%`, background: color }}
									/>
								</div>
								<span className={styles.cost}>
									{formatCostPerKm(item.costPerKm)}
								</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="top-cost-per-km-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default TopCostPerKm;
